/**
 * The route table and the app shell.
 *
 * Every page the customer can reach is mounted here inside the shell
 * (NavBarTop + SideNav). Routes nest in two guards, and the order matters:
 *
 *   ProtectedRoutes  - signed in at all; anonymous visitors go to /login.
 *   ModuleRoute      - has access to this module; looked up per route, so a
 *                      page behind it renders only after that answer arrives.
 *
 * A page added outside those guards is reachable by anyone with the URL.
 */
import "./App.css";
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Box from "@mui/material/Box";
import NavBarTop from "./components/NavBarTop";
import SideNav from "./components/SideNav";
import HomePage from "./pages/Home";
import AboutPage from "./pages/About";
import ContactPage from "./pages/Contact";
import LoginPage from "./pages/Login";
import ProfilePage from "./pages/Profile";
import SystemStatus from "./pages/SystemStatus";
import PasswordChange from "./pages/PasswordChange";
import PasswordForgotten from "./pages/PasswordForgotten";
import PasswordReset from "./pages/PasswordReset";
import UserAdmin from "./pages/UserAdmin";
import ColorExplorationHome from "./pages/colorExploration/ColorExplorationHome";
import ColorExplorationSetup from "./pages/colorExploration/ColorExplorationSetup";
import { EXPLORATION_SETUP_ROUTE } from "./helper/explorationRoutes";
import ProtectedRoutes from "./components/ProtectedRoutes";
import ModuleRoute from "./components/ModuleRoute";
import AdminRoute from "./components/AdminRoute";
import {
  MODULE_COLOR_EXPLORER,
  MODULE_IMAGE_REPRODUCTION,
  MODULE_SELECTION_EXPLORER,
} from "./helper/moduleIds";
import ColorExplorationCalibrationCamera from "./pages/colorExploration/ColorExplorationCalibrationCamera";
import CameraCalibrationsOverview from "./pages/colorExploration/CameraCalibrationsOverview";
import ColorExplorationColorResolution from "./pages/colorExploration/ColorExplorationColorResolution"
import ColorExplorationColorColor from "./pages/colorExploration/ColorExplorationColorColor";
import ColorExplorationColorSummary from "./pages/colorExploration/ColorExplorationColorSummary";
import ColorExplorationAnalyse from "./pages/colorExploration/ColorExplorationAnalyse";
import SubstratesOverview from "./pages/colorExploration/SubstratesOverview";
import LasersOverview from "./pages/colorExploration/LasersOverview";
import ColorExplorationMeasureWidth from "./pages/colorExploration/ColorExplorationMeasureWidth";
import ColorSelectionHome from "./pages/colorSelection/ColorSelectionHome";
import ColorSelectionSelectExplorations from "./pages/colorSelection/ColorSelectionSelectExplorations";
import ColorSelectionSetUpName from "./pages/colorSelection/ColorSelectionSetUpName";
import ColorSelectionTypeChoice from "./pages/colorSelection/ColorSelectionTypeChoice";
import ColorSelectionPruning from "./pages/colorSelection/ColorSelectionPruning";
import ColorSelectionComposition from "./pages/colorSelection/ColorSelectionComposition";
import ColorSelectionPrepass from "./pages/colorSelection/ColorSelectionPrepass";
import ColorSelectionSpatial from "./pages/colorSelection/ColorSelectionSpatial";
import ColorSelectionRepeatabilityFilter from "./pages/colorSelection/ColorSelectionRepeatabilityFilter";
import ColorSelectionOverview from "./pages/colorSelection/ColorSelectionOverview";
import ColorSelectionManualPlates from "./pages/colorSelection/ColorSelectionManualPlates";
import ColorSelectionManualSearch from "./pages/colorSelection/ColorSelectionManualSearch";
import ColorSelectionModeContrast from "./pages/colorSelection/ColorSelectionModeContrast";
import ColorSelectionWidthGroups from "./pages/colorSelection/ColorSelectionWidthGroups";
import ColorSelectionPrimaries from "./pages/colorSelection/ColorSelectionPrimaries";
import ColorSelectionPredictability from "./pages/colorSelection/ColorSelectionPredictability";
import ColorReproductionTypeChoice from "./pages/colorReproduction/ColorReproductionTypeChoice";
import ColorReproductionHome from "./pages/colorReproduction/ColorReproductionHome";
import ColorReproductionNew from "./pages/colorReproduction/ColorReproductionNew";
import ColorReproductionSetup from "./pages/colorReproduction/ColorReproductionSetup";
import ColorReproductionEdit from "./pages/colorReproduction/ColorReproductionEdit";
import ColorReproductionGamutMapping from "./pages/colorReproduction/ColorReproductionGamutMapping";
import ColorReproductionGradient from "./pages/colorReproduction/ColorReproductionGradient";
import ColorReproductionMarking from "./pages/colorReproduction/ColorReproductionMarking";
import SelectionSetupFlow from "./pages/colorSelection/SelectionSetupFlow";
import ProjectsHome from "./pages/projects/ProjectsHome";
import { PROJECTS_ROUTE } from "./helper/projects";
import { REPRODUCTION_KIND_SLUGS, REPRODUCTIONS_BASE } from "./helper/reproductionFlow";
import { SELECTION_KINDS, SELECTION_KIND_KEYS, SELECTIONS_BASE } from "./helper/selectionFlow";


function App() {
  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Oraclase</title>
        <link rel="canonical" href="http://mysite.com/example" />
        <meta name="description" content="Oraclase Application" />
      </Helmet>
      <Router>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            // Viewport-bound: the side menu keeps the full height and only
            // the center content scrolls.
            height: "100vh",
            width: "100%",
            boxSizing: "border-box",
            overflow: "hidden",
            "@supports (height: 100dvh)": {
              height: "100dvh",
            },
          }}
        >
          <NavBarTop></NavBarTop>
          <Box sx={{ display: "flex", flex: "1 1 auto", minWidth: 0, minHeight: 0, width: "100%", alignItems: "stretch" }}>
          <SideNav />
          {/* scrollbarGutter: the gutter stays reserved while a page is
              shorter than the viewport, so centred content does not jump
              sideways when the scrollbar comes and goes between loads. */}
          <Box component="main" sx={{ flex: "1 1 auto", minWidth: 0, overflowY: "auto", scrollbarGutter: "stable" }}>
        <Routes>
          <Route path="/" exact element={<HomePage></HomePage>}></Route>
          <Route path="/about" exact element={<AboutPage></AboutPage>}></Route>
          <Route
            path="/contact"
            exact
            element={<ContactPage></ContactPage>}
          ></Route>
          <Route path="/login" exact element={<LoginPage></LoginPage>}></Route>
          <Route
            path="/passwordforgotten"
            exact
            element={<PasswordForgotten></PasswordForgotten>}
          ></Route>
          <Route
            path="/passwordreset/:uid/:token"
            element={<PasswordReset></PasswordReset>}
          ></Route>
          <Route element={<ProtectedRoutes />}>
            <Route
              path="/profile"
              exact
              element={<ProfilePage></ProfilePage>}
            ></Route>
            <Route
              path="/passwordchange"
              exact
              element={<PasswordChange></PasswordChange>}
            ></Route>
            {/* Projects: folders of explorations, selections and reproductions,
                spanning the modules; the open project is in the URL. */}
            <Route path={PROJECTS_ROUTE} element={<ProjectsHome />} />
            <Route path={`${PROJECTS_ROUTE}/:projectId`} element={<ProjectsHome />} />
            {/* Administration: Users for administrators, System for
                administrators and developers. */}
            <Route element={<AdminRoute />}>
            <Route path="/users" exact element={<UserAdmin></UserAdmin>}></Route>
            </Route>
            <Route element={<AdminRoute developers />}>
            <Route path="/system" exact element={<SystemStatus></SystemStatus>}></Route>
            </Route>
            {/* Resources, shared by all modules. */}
            <Route
              path="/substrates"
              exact
              element={<SubstratesOverview></SubstratesOverview>}
            ></Route>
            <Route
              path="/lasers"
              exact
              element={<LasersOverview></LasersOverview>}
            ></Route>
            <Route
              path="/colorexplorationcalibrationcamera"
              exact
              element={<ColorExplorationCalibrationCamera></ColorExplorationCalibrationCamera>}
            ></Route>
            <Route
              path="/cameracalibrations"
              exact
              element={<CameraCalibrationsOverview></CameraCalibrationsOverview>}
            ></Route>
            {/* Color Explorer pages: only for users with a UserAppAccess row.
                An exploration's pages carry its id in the URL
                (/colorexplorations/<id>/<step>); the New exploration page
                (/colorexplorationsetup) holds the draft until the exploration
                exists, and the old wizard step URLs lead there. */}
            <Route element={<ModuleRoute moduleId={MODULE_COLOR_EXPLORER} />}>
            <Route path="/colorexplorationhome" element={<ColorExplorationHome />} />
            <Route path="/colorexplorationsetup" element={<ColorExplorationSetup />} />
            <Route path="/colorexplorationsetup/*" element={<Navigate to={EXPLORATION_SETUP_ROUTE} replace />} />
            <Route path="/colorexplorations/:explorationId">
              <Route path="resolution" element={<ColorExplorationColorResolution />} />
              <Route path="width" element={<ColorExplorationMeasureWidth />} />
              <Route path="color" element={<ColorExplorationColorColor />} />
              <Route path="summary" element={<ColorExplorationColorSummary />} />
              <Route path="analyse" element={<ColorExplorationAnalyse />} />
            </Route>
            </Route>
            {/* Color Reproduction pages: only for users with a UserAppAccess row.
                Per kind: its list, its create page and, with the id in the URL,
                the steps of one reproduction (/colorreproductions/<kind>/<id>/<step>). */}
            <Route element={<ModuleRoute moduleId={MODULE_IMAGE_REPRODUCTION} />}>
            <Route path="/colorreproductiontype" element={<ColorReproductionTypeChoice />} />
            {/* One list for every kind; the old per-kind list URLs lead there. */}
            <Route path={REPRODUCTIONS_BASE} element={<ColorReproductionHome />} />
            {REPRODUCTION_KIND_SLUGS.map((kind) => (
              <Route key={kind} path={`/colorreproductions/${kind}`}>
                <Route index element={<Navigate to={REPRODUCTIONS_BASE} replace />} />
                <Route path="new" element={<ColorReproductionNew key={kind} kind={kind} />} />
                <Route path=":reproductionId">
                  <Route path="setup" element={<ColorReproductionSetup />} />
                  <Route path="edit" element={<ColorReproductionEdit />} />
                  <Route path="gamut" element={<ColorReproductionGamutMapping />} />
                  <Route path="gradient" element={<ColorReproductionGradient />} />
                  <Route path="marking" element={<ColorReproductionMarking />} />
                </Route>
              </Route>
            ))}
            </Route>
            {/* Selection Explorer pages: only for users with a UserAppAccess row.
                Per kind: its list, the create wizard (draft in
                SelectionSetupFlow) and, with the id in the URL, the steps of
                one selection (/colorselections/<kind>/<id>/<step>). */}
            <Route element={<ModuleRoute moduleId={MODULE_SELECTION_EXPLORER} />}>
            <Route path="/colorselectiontype" element={<ColorSelectionTypeChoice />} />
            {/* One list for every type; the old per-type list URLs lead there. */}
            <Route path={SELECTIONS_BASE} element={<ColorSelectionHome />} />
            {SELECTION_KIND_KEYS.map((kindKey) => (
              <Route key={kindKey} path={SELECTION_KINDS[kindKey].listRoute}>
                <Route index element={<Navigate to={SELECTIONS_BASE} replace />} />
                <Route path="new" element={<SelectionSetupFlow />}>
                  <Route path="name" element={<ColorSelectionSetUpName />} />
                  <Route path="explorations" element={<ColorSelectionSelectExplorations />} />
                </Route>
                <Route path=":selectionId">
                  <Route path="explorations" element={<ColorSelectionSelectExplorations />} />
                  <Route path="pruning" element={<ColorSelectionPruning />} />
                  <Route path="composition" element={<ColorSelectionComposition />} />
                  <Route path="prepass" element={<ColorSelectionPrepass />} />
                  <Route path="spatial" element={<ColorSelectionSpatial />} />
                  <Route path="repeatability" element={<ColorSelectionRepeatabilityFilter />} />
                  <Route path="modecontrast" element={<ColorSelectionModeContrast />} />
                  <Route path="overview" element={<ColorSelectionOverview />} />
                  <Route path="plates" element={<ColorSelectionManualPlates />} />
                  <Route path="search" element={<ColorSelectionManualSearch />} />
                  <Route path="widthgroups" element={<ColorSelectionWidthGroups />} />
                  <Route path="primaries" element={<ColorSelectionPrimaries />} />
                  <Route path="predictability" element={<ColorSelectionPredictability />} />
                </Route>
              </Route>
            ))}
            </Route>
          </Route>
        </Routes>
          </Box>
          </Box>
        </Box>
      </Router>
    </div>
  );
}

export default App;
